import { num, type ComponentForm, type FieldDef } from '../component-types.js';
import { MESH_SOURCE_SPEC } from '../../../lib/asset-types.js';

/**
 * `particleSystem` (Unity's Shuriken-style Particle System): one nested
 * object per module. Curve values (MinMaxCurve) use the `curve` field kind and
 * colors (MinMaxGradient) the `gradient` field kind (see ../fields/).
 * Optional modules are off while absent. See engine/docs/systems/particles.md.
 */

type CurveOpts = { step?: number; min?: number; max?: number; hint?: string };

/** MinMaxCurve field (constant / random / curve / random between two curves). */
const curve = (key: string, label: string, opts: CurveOpts = {}): FieldDef => ({
  key,
  label,
  type: { kind: 'curve', step: opts.step, min: opts.min, max: opts.max },
  hint: opts.hint,
});
/** MinMaxGradient field (color / gradient / random colors / random gradients / random color). */
const color = (key: string, label: string, hint?: string): FieldDef => ({ key, label, type: { kind: 'gradient' }, hint });
const enabled: FieldDef = { key: 'enabled', label: 'Enabled', type: { kind: 'bool' } };
const space = (label = 'Space'): FieldDef => ({ key: 'space', label, type: { kind: 'enum', options: ['local', 'world'] } });

const main: FieldDef[] = [
  { key: 'duration', label: 'Duration', type: num(0.1, { min: 0.001 }) },
  { key: 'looping', label: 'Looping', type: { kind: 'bool' } },
  { key: 'prewarm', label: 'Prewarm', type: { kind: 'bool' }, hint: 'Looping systems start as if one cycle already ran.' },
  curve('startDelay', 'Start delay', { min: 0, hint: 'Seconds before the first cycle (curves use the cycle time).' }),
  curve('startLifetime', 'Start lifetime', { min: 0, hint: 'Seconds; curves are sampled at the normalized cycle time.' }),
  curve('startSpeed', 'Start speed'),
  curve('startSize', 'Start size', { step: 0.05, min: 0 }),
  curve('startRotation', 'Start rotation (deg)', { step: 1 }),
  color('startColor', 'Start color', 'Curves and gradients are sampled at the normalized cycle time.'),
  curve('gravityModifier', 'Gravity modifier', { hint: 'Multiplier on settings.physics.gravity.' }),
  { key: 'simulationSpace', label: 'Simulation space', type: { kind: 'enum', options: ['local', 'world'] } },
  { key: 'simulationSpeed', label: 'Simulation speed', type: num(0.1, { min: 0 }) },
  { key: 'scalingMode', label: 'Scaling mode', type: { kind: 'enum', options: ['hierarchy', 'local', 'shape'] } },
  { key: 'playOnAwake', label: 'Play on awake', type: { kind: 'bool' } },
  { key: 'maxParticles', label: 'Max particles', type: num(1, { min: 1, integer: true }) },
  { key: 'seed', label: 'Seed', type: num(1, { min: 0, integer: true }), hint: '0 = derived from the entity id (deterministic either way).' },
  { key: 'stopAction', label: 'Stop action', type: { kind: 'enum', options: ['none', 'destroy'] } },
];

const emission: FieldDef[] = [
  enabled,
  curve('rateOverTime', 'Rate over time', { step: 1, min: 0, hint: 'Particles per second (curves use the cycle time).' }),
  curve('rateOverDistance', 'Rate over distance', { step: 1, min: 0, hint: 'Particles per meter moved.' }),
  {
    key: 'bursts',
    label: 'Bursts',
    type: { kind: 'json' },
    hint: '[{ "time": 0, "count": 30, "cycles": 1, "interval": 0.01, "probability": 1 }] (cycles 0 = repeat every interval).',
  },
];

const shapeIs = (...types: string[]) => (v: Record<string, unknown>) => types.includes(String(v.type ?? 'cone'));

const shape: FieldDef[] = [
  enabled,
  {
    key: 'type',
    label: 'Shape',
    type: { kind: 'enum', options: ['sphere', 'hemisphere', 'cone', 'box', 'circle', 'edge', 'rectangle', 'mesh'] },
    hint: 'Y-up: cones and hemispheres open towards +Y, circles lie in XZ, edges run along X.',
  },
  { key: 'radius', label: 'Radius', type: num(0.05, { min: 0 }), showIf: shapeIs('sphere', 'hemisphere', 'cone', 'circle', 'edge') },
  {
    key: 'radiusThickness',
    label: 'Radius thickness',
    type: num(0.05, { min: 0, max: 1 }),
    hint: '0 = surface / rim only, 1 = whole volume.',
    showIf: shapeIs('sphere', 'hemisphere', 'cone', 'circle'),
  },
  { key: 'angle', label: 'Angle', type: num(1, { min: 0, max: 90 }), showIf: shapeIs('cone') },
  { key: 'arc', label: 'Arc', type: num(5, { min: 0, max: 360 }), showIf: shapeIs('sphere', 'hemisphere', 'cone', 'circle') },
  { key: 'boxSize', label: 'Box size', type: { kind: 'vec3', step: 0.1 }, showIf: shapeIs('box', 'rectangle') },
  {
    key: 'mesh',
    label: 'Mesh',
    type: { kind: 'path', ...MESH_SOURCE_SPEC },
    hint: 'Mesh source like "primitive:sphere", "model:models/x.model.json" or "gltf:models/x.glb".',
    showIf: shapeIs('mesh'),
  },
  { key: 'position', label: 'Position', type: { kind: 'vec3', step: 0.1 } },
  { key: 'rotation', label: 'Rotation', type: { kind: 'vec3', step: 1 } },
  { key: 'scale', label: 'Scale', type: { kind: 'vec3', step: 0.1 } },
  { key: 'randomizeDirection', label: 'Randomize direction', type: num(0.05, { min: 0, max: 1 }) },
  { key: 'alignToDirection', label: 'Align to direction', type: { kind: 'bool' } },
];

const xyz = (): FieldDef[] => [curve('x', 'X'), curve('y', 'Y'), curve('z', 'Z')];

const renderer: FieldDef[] = [
  {
    key: 'renderMode',
    label: 'Render mode',
    type: { kind: 'enum', options: ['billboard', 'stretchedBillboard', 'horizontalBillboard', 'verticalBillboard', 'mesh'] },
  },
  {
    key: 'material',
    label: 'Material',
    type: { kind: 'path', suffix: '.material.json' },
    hint: 'Unlit or none = built-in particle shader (texture * particle color). Standard / toon / custom shader materials draw lit quads or meshes (tinted by the particle color, texture sheet frames applied).',
  },
  { key: 'texture', label: 'Texture', type: { kind: 'path', suffix: ['.png', '.jpg', '.jpeg', '.pixel.json'] } },
  { key: 'blend', label: 'Blend', type: { kind: 'enum', options: ['alpha', 'additive', 'premultiply'] } },
  {
    key: 'mesh',
    label: 'Mesh',
    type: { kind: 'path', ...MESH_SOURCE_SPEC },
    showIf: (v) => v.renderMode === 'mesh',
  },
  { key: 'sortMode', label: 'Sort mode', type: { kind: 'enum', options: ['none', 'distance', 'oldestInFront', 'youngestInFront'] } },
  { key: 'minParticleSize', label: 'Min size (screen)', type: num(0.01, { min: 0, max: 10 }) },
  { key: 'maxParticleSize', label: 'Max size (screen)', type: num(0.01, { min: 0, max: 10 }) },
  { key: 'lengthScale', label: 'Length scale', type: num(0.1), showIf: (v) => v.renderMode === 'stretchedBillboard' },
  { key: 'velocityScale', label: 'Velocity scale', type: num(0.01), showIf: (v) => v.renderMode === 'stretchedBillboard' },
  { key: 'castShadows', label: 'Cast shadows', type: { kind: 'bool' } },
];

const form: ComponentForm = {
  key: 'particleSystem',
  label: 'Particle System',
  icon: 'particles',
  order: 95,
  defaults: {
    main: { duration: 5, looping: true, startLifetime: 2, startSpeed: 2, startSize: 0.2, startColor: [1, 1, 1, 1], simulationSpace: 'world' },
    emission: { rateOverTime: 20 },
    shape: { type: 'cone', radius: 0.3, angle: 20 },
    colorOverLifetime: { color: { gradient: { alphaKeys: [[0, 1], [1, 0]] } } },
    renderer: { renderMode: 'billboard', blend: 'alpha' },
  },
  fields: [
    { key: 'main', label: 'Main', type: { kind: 'object', fields: main } },
    { key: 'emission', label: 'Emission', type: { kind: 'object', fields: emission } },
    { key: 'shape', label: 'Shape', type: { kind: 'object', fields: shape } },
    {
      key: 'velocityOverLifetime',
      label: 'Velocity over lifetime',
      type: { kind: 'object', fields: [enabled, ...xyz(), space(), curve('radial', 'Radial'), curve('speedModifier', 'Speed modifier')] },
    },
    {
      key: 'limitVelocityOverLifetime',
      label: 'Limit velocity',
      type: {
        kind: 'object',
        fields: [
          enabled,
          curve('speed', 'Speed'),
          { key: 'dampen', label: 'Dampen', type: num(0.05, { min: 0, max: 1 }) },
          curve('drag', 'Drag'),
          { key: 'multiplyDragBySize', label: 'Drag × size', type: { kind: 'bool' } },
          { key: 'multiplyDragByVelocity', label: 'Drag × velocity', type: { kind: 'bool' } },
        ],
      },
    },
    { key: 'forceOverLifetime', label: 'Force over lifetime', type: { kind: 'object', fields: [enabled, ...xyz(), space()] } },
    { key: 'colorOverLifetime', label: 'Color over lifetime', type: { kind: 'object', fields: [enabled, color('color', 'Color')] } },
    { key: 'sizeOverLifetime', label: 'Size over lifetime', type: { kind: 'object', fields: [enabled, curve('size', 'Size')] } },
    {
      key: 'rotationOverLifetime',
      label: 'Rotation over lifetime',
      type: { kind: 'object', fields: [enabled, curve('angularVelocity', 'Angular velocity (deg/s)', { step: 5 })] },
    },
    {
      key: 'noise',
      label: 'Noise',
      type: {
        kind: 'object',
        fields: [
          enabled,
          curve('strength', 'Strength'),
          { key: 'frequency', label: 'Frequency', type: num(0.05, { min: 0 }) },
          { key: 'scrollSpeed', label: 'Scroll speed', type: num(0.05) },
          { key: 'octaves', label: 'Octaves', type: num(1, { min: 1, max: 4, integer: true }) },
          { key: 'positionAmount', label: 'Position amount', type: num(0.05, { min: 0 }) },
          { key: 'rotationAmount', label: 'Rotation amount', type: num(1) },
          { key: 'sizeAmount', label: 'Size amount', type: num(0.05, { min: 0, max: 1 }) },
        ],
      },
    },
    {
      key: 'collision',
      label: 'Collision',
      type: {
        kind: 'object',
        fields: [
          enabled,
          { key: 'type', label: 'Type', type: { kind: 'enum', options: ['planes', 'world'] } },
          {
            key: 'planes',
            label: 'Planes',
            type: { kind: 'json' },
            hint: 'Entity ids; each entity\'s XZ plane (normal = its up axis) is a collision plane.',
            showIf: (v) => (v.type ?? 'planes') === 'planes',
          },
          {
            key: 'collidesWith',
            label: 'Collides with',
            type: { kind: 'tags' },
            hint: 'Layer names hit in world mode (empty = all). World collision uses physics raycasts in play mode.',
            showIf: (v) => v.type === 'world',
          },
          curve('bounce', 'Bounce', { step: 0.05, min: 0 }),
          curve('dampen', 'Dampen', { step: 0.05, min: 0, max: 1 }),
          curve('lifetimeLoss', 'Lifetime loss', { step: 0.05, min: 0, max: 1 }),
          { key: 'minKillSpeed', label: 'Min kill speed', type: num(0.1, { min: 0 }) },
          { key: 'radiusScale', label: 'Radius scale', type: num(0.05, { min: 0 }) },
        ],
      },
    },
    {
      key: 'subEmitters',
      label: 'Sub emitters',
      type: {
        kind: 'object',
        fields: [
          enabled,
          {
            key: 'emitters',
            label: 'Emitters',
            type: { kind: 'json' },
            hint: '[{ "type": "birth" | "collision" | "death", "emitter": "<entity id with a particleSystem>", "count": 0, "probability": 1, "inherit": ["color", "size", "rotation", "lifetime", "velocity"] }]',
          },
        ],
      },
    },
    {
      key: 'textureSheetAnimation',
      label: 'Texture sheet animation',
      type: {
        kind: 'object',
        fields: [
          enabled,
          { key: 'tiles', label: 'Tiles [x, y]', type: { kind: 'json' } },
          { key: 'animation', label: 'Animation', type: { kind: 'enum', options: ['wholeSheet', 'singleRow'] } },
          { key: 'row', label: 'Row', type: num(1, { min: 0, integer: true }), showIf: (v) => v.animation === 'singleRow' },
          { key: 'randomRow', label: 'Random row', type: { kind: 'bool' }, showIf: (v) => v.animation === 'singleRow' },
          curve('frameOverTime', 'Frame over time', { step: 0.05, hint: '0..1 through the frames over the lifetime (default a 0 → 1 ramp).' }),
          curve('startFrame', 'Start frame', { step: 1, min: 0 }),
          { key: 'cycles', label: 'Cycles', type: num(0.5, { min: 0 }) },
        ],
      },
    },
    { key: 'renderer', label: 'Renderer', type: { kind: 'object', fields: renderer } },
  ],
};

export default form;
